import { Box, Drawer, Stack } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import Logo from 'components/Logo';
import Scrollbar from 'components/Scrollbar';
import { NAVBAR } from 'config';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import useResponsive from 'hooks/useResponsive';
import NavSectionVertical from 'layouts/vertical';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RootState, useSelector } from 'redux/store';
import cssStyles from 'utils/cssStyles';
import CollapseButton from './CollapseButton';
import menuBar from './navConfig';
import { CustomTheme } from 'enums/customeTheme';
import { NavbarAccount } from './NavBarAccount';

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useResponsive('up', 'lg');
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse } = useCollapseDrawer();
  const customTheme = useSelector((state: RootState) => state.customer.customer?.customTheme);
  const showSageBranding = customTheme === CustomTheme.Sage && !isCollapse;

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderContent = (
    <>
      <Scrollbar
        sx={{
          height: 1,
          '& .simplebar-content': {
            height: 1,
            display: 'flex',
            flexDirection: 'column',
            // overflowX: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            // overflow: "hidden",
            // width: 90,
            justifyContent: 'space-between',
          }}
        >
          <Stack
            spacing={0.5}
            sx={{
              pt: 1,
              pb: 2,
              flexShrink: 0,
              ...(isCollapse && { alignItems: 'center' }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2.5,
              }}
            >
              <Logo isCollapse={isCollapse} isDrawer />
            </Box>
            <NavSectionVertical
              sx={{ px: 2.5 }}
              navConfig={menuBar().navConfig}
              isCollapse={isCollapse}
            />
          </Stack>
          <Box sx={{ p: 2 }}>
            <NavbarAccount isCollapse={isCollapse} />
          </Box>
        </Box>
      </Scrollbar>
      {isDesktop && (
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            right: -14,
            zIndex: 9999,
          }}
        >
          <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
        </Box>
      )}
    </>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              overflow: 'visible',
              ...(customTheme === CustomTheme.Sage && {
                borderRight: `4px solid ${theme.palette.sage_green}`,
              }),
              width: NAVBAR.DASHBOARD_WIDTH,
              // borderRightStyle: "dashed",
              bgcolor: 'background.neutral',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
